import { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/States'
import { useAuth } from '@/auth/useAuth'
import { safeNext } from '@/routes/guards'

/**
 * One control. No email/password fields, and no error enumeration — the
 * failure copy does not distinguish "no account" from "wrong domain"
 * (docs/03-page-specs.md §1).
 *
 * Domain restriction to @waybettermarketing.com is enforced by an Auth0
 * Action, not here.
 */
export function LoginPage() {
  const { user, loading, error, signIn } = useAuth()
  const [params] = useSearchParams()
  const [busy, setBusy] = useState(false)

  const next = safeNext(params.get('next'))

  if (!loading && user) return <Navigate to={next} replace />

  async function onSignIn() {
    setBusy(true)
    try {
      // `next` goes INTO the call, and nothing navigates after it. Against Auth0
      // this leaves the page entirely and comes back at /callback, so a
      // navigate() here would run against a document that is already unloading;
      // the return path is carried in appState instead. Against the dev mock it
      // resolves in place and the <Navigate> guard above fires on the next
      // render. One code path, both backings.
      await signIn(next)
    } catch {
      // Surfaced through `error` on the auth context.
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-canvas px-6">
      <div className="mb-5 flex items-center gap-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-[5px] bg-brand-red font-head text-[15px] font-semibold text-white">
          W
        </span>
        <span className="font-head text-[16px] font-semibold">The Hive - Waybetter Marketing</span>
      </div>

      <Card className="w-full max-w-[380px]">
        <div className="space-y-4 p-6">
          <div>
            <h1 className="text-[18px] leading-7">Sign in</h1>
            <p className="mt-1 text-[13px] leading-5 text-ink-muted">
              Waybetter staff only.
            </p>
          </div>

          {error && <ErrorState title="Sign-in failed" message={error} className="py-4" />}

          <Button variant="primary" className="w-full" loading={busy} onClick={onSignIn}>
            Continue with Google
          </Button>

          <p className="text-[12px] leading-4 text-ink-faint">
            Access is granted per client. If a client you expect is missing after signing in, ask
            an administrator.
          </p>
        </div>
      </Card>
    </div>
  )
}
